// Shared form primitives for the Sponsor Store editors (Placements + Packages).
// Extracted when the pricing editor was split onto its own tab so both halves keep the
// same look without one importing from the other.
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react'

// ---- styles ----

export const inputStyle = {
  width: '100%', background: 'var(--color-bg)', color: 'var(--color-text)',
  border: '1px solid var(--color-border)', borderRadius: 6, padding: '8px 12px', fontSize: 13, boxSizing: 'border-box',
}
export const cardStyle = { background: 'var(--color-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 20, marginBottom: 16 }
export const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }
export const hintStyle = { color: 'var(--color-text-muted)', fontSize: 11, margin: '4px 0 0', lineHeight: 1.45 }
export const smallBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 5, background: 'transparent',
  color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', borderRadius: 6,
  padding: '5px 9px', fontSize: 12, cursor: 'pointer',
}
export const iconBtn = { ...smallBtn, padding: '4px 6px' }
export const textareaStyle = { ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }
export const subCardStyle = { border: '1px solid var(--color-border)', borderRadius: 8, padding: 12, marginBottom: 8, background: 'var(--color-bg)' }
export const rowHeadStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }
export const rowLabelStyle = { fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }

// ---- fields ----

export function Field({ label, value, onChange, hint, placeholder }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} spellCheck={false} autoComplete="off" />
      {hint && <p style={hintStyle}>{hint}</p>}
    </div>
  )
}

export function Area({ label, value, onChange, hint, rows = 3 }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows} style={textareaStyle} />
      {hint && <p style={hintStyle}>{hint}</p>}
    </div>
  )
}

// Bulleted list of short strings — used for an offering's format / where / delivery columns.
export function StringListEditor({ label, items, onChange, hint }) {
  const list = Array.isArray(items) ? items : []
  const move = (i, dir) => {
    const to = i + dir
    if (to < 0 || to >= list.length) return
    const next = [...list]
    const [m] = next.splice(i, 1)
    next.splice(to, 0, m)
    onChange(next)
  }
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      {list.map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <textarea
            value={s}
            onChange={e => onChange(list.map((x, j) => (j === i ? e.target.value : x)))}
            rows={2}
            style={{ ...textareaStyle, fontSize: 12, lineHeight: 1.45 }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <button type="button" title="Move up" onClick={() => move(i, -1)} style={iconBtn}><ChevronUp size={12} /></button>
            <button type="button" title="Move down" onClick={() => move(i, 1)} style={iconBtn}><ChevronDown size={12} /></button>
            <button type="button" title="Remove" onClick={() => onChange(list.filter((_, j) => j !== i))} style={iconBtn}><Trash2 size={12} /></button>
          </div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...list, ''])} style={smallBtn}>
        <Plus size={12} /> Add line
      </button>
      {hint && <p style={hintStyle}>{hint}</p>}
    </div>
  )
}


// Shown/Hidden switch with an explanatory caption beside it.
export function Toggle({ on, onClick, children }) {
  return (
    <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
      <button
        type="button"
        onClick={onClick}
        style={{
          minWidth: 92, background: on ? 'var(--color-primary, #dc2626)' : 'transparent',
          color: on ? '#fff' : 'var(--color-text-muted)',
          border: '1px solid ' + (on ? 'transparent' : 'var(--color-border)'),
          borderRadius: 6, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}
      >
        {on ? 'Shown' : 'Hidden'}
      </button>
      <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{children}</span>
    </div>
  )
}
